import * as yup from "yup";
import {stripHtml} from "./constants";

/** If missing a scheme (e.g. `www.site.com`), assume https. Preserves mailto:, tel:, etc. */
function normalizeButtonLinkCandidate(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const s = value.trim();
  if (!s) return s;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return s;
  if (s.startsWith("//")) return `https:${s}`;
  return `https://${s}`;
}

export const createAutomationSchema = yup.object({
  automationName: yup.string().required("Automation name is required"),
  subject: yup.string().required("Subject is required"),
  body: yup
    .string()
    .required("Body is required")
    .test("not-empty-html", "Body is required", (value) => {
      if (!value) return false;
      return stripHtml(value).length > 0;
    }),
  addCustomButton: yup.boolean().default(false),
  buttonText: yup.string().when("addCustomButton", {
    is: true,
    then: (schema) => schema.required("Button text is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  buttonLink: yup.string().when("addCustomButton", {
    is: true,
    then: (schema) =>
      schema
        .transform(normalizeButtonLinkCandidate)
        .required("Button link is required")
        .url("Please enter a valid URL"),
    otherwise: (schema) => schema.notRequired(),
  }),
  trigger: yup.string().required("Trigger is required"),
  offerings: yup
    .array()
    .of(yup.string())
    .min(1, "Please select at least one offering")
    .required("Please select at least one offering"),
  sendTiming: yup.string().default("immediately"),
  delayValue: yup.number().when(["trigger", "sendTiming"], {
    is: (trigger: string, sendTiming: string) =>
      trigger === "abandoned_purchase" || sendTiming === "custom_delay",
    then: (schema) =>
      schema
        .required("Delay value is required")
        .typeError("Must be a number")
        .test("valid-delay", function (value) {
          const {delayUnit, trigger} = this.parent;
          if (!value) return false;
          if (delayUnit === "minutes") {
            const minMinutes = trigger === "abandoned_purchase" ? 30 : 5;
            if (value < minMinutes || value > 60) {
              return this.createError({
                message: `Minutes must be between ${minMinutes} and 60`,
              });
            }
            return true;
          }
          if (delayUnit === "hours") {
            if (value < 1 || value > 72) {
              return this.createError({
                message: "Hours must be between 1 and 72",
              });
            }
            return true;
          }
          if (delayUnit === "days") {
            if (value < 1 || value > 30) {
              return this.createError({
                message: "Days must be between 1 and 30",
              });
            }
            return true;
          }
          return true;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  delayUnit: yup.string().when(["trigger", "sendTiming"], {
    is: (trigger: string, sendTiming: string) =>
      trigger === "abandoned_purchase" || sendTiming === "custom_delay",
    then: (schema) => schema.required("Delay unit is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const draftAutomationSchema = createAutomationSchema.shape({
  offerings: yup.array().of(yup.string()).default([]),
  buttonText: yup.string().notRequired(),
  buttonLink: yup
    .string()
    .transform(normalizeButtonLinkCandidate)
    .notRequired()
    .test("optional-url", "Please enter a valid URL", (value) => {
      if (!value || value.trim() === "") return true;
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }),
});

export type CreateAutomationFormValues = yup.InferType<typeof createAutomationSchema>;
